import QueuesService from '@/services/QueuesService'

function str(v) {
  return String(v ?? '').trim()
}

function normalizeQueue(q) {
  if (!q || typeof q !== 'object') return null

  return {
    id: str(q.id),                 // EndpointID
    queue_name: str(q.queue_name), // Q########
    name: str(q.name),             // Description (human name)

    strategy: str(q.strategy || 'ringall'),
    maxCalls: Number.isFinite(q.maxCalls) ? q.maxCalls : 0,
    ringTimeout: Number.isFinite(q.ringTimeout) ? q.ringTimeout : 20,
    wrapupTime: Number.isFinite(q.wrapupTime) ? q.wrapupTime : 0,
    slaSeconds: Number.isFinite(q.slaSeconds) ? q.slaSeconds : 0,
    joinEmpty: str(q.joinEmpty || 'yes'),
    leaveWhenEmpty: str(q.leaveWhenEmpty || 'no'),
    moh: str(q.moh || 'default'),

    overflow: {
      timeout: q?.overflow?.timeout ?? null,
      rollTo: q?.overflow?.rollTo ?? null
    },

    members: Array.isArray(q.members) ? q.members.map(m => ({
      id: Number.isFinite(m.id) ? m.id : (m.uniqueid ?? null),
      interface: str(m.interface),
      name: str(m.name || m.membername),
      ext: str(m.ext),
      penalty: Number.isFinite(m.penalty) ? m.penalty : 0,
      paused: !!m.paused,
      wrapuptime: Number.isFinite(m.wrapuptime) ? m.wrapuptime : 0
    })) : []
  }
}

export default {
  namespaced: true,

  state: () => ({
    items: [],
    current: null,
    loading: false,
    saving: false,
    error: null
  }),

  getters: {
    list: (s) => s.items,
    current: (s) => s.current,
    byId: (s) => (id) => s.items.find(x => x.id === str(id)) || null
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_SAVING(state, v) { state.saving = !!v },
    SET_ERROR(state, v) { state.error = v || null },

    SET_LIST(state, rows) { state.items = Array.isArray(rows) ? rows : [] },

    UPSERT_ONE(state, row) {
      const q = normalizeQueue(row)
      if (!q?.id) return

      const i = state.items.findIndex(x => x.id === q.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], ...q })
      else state.items.unshift(q)

      if (state.current?.id === q.id) {
        state.current = { ...state.current, ...q }
      }
    },

    SET_CURRENT(state, row) {
      state.current = row ? normalizeQueue(row) : null
    },

    REMOVE_ONE(state, endpointId) {
      const id = str(endpointId)
      state.items = state.items.filter(x => x.id !== id)
      if (state.current?.id === id) state.current = null
    },

    SET_CURRENT_MEMBERS(state, members) {
      if (!state.current) return
      state.current = { ...state.current, members: Array.isArray(members) ? members : [] }

      const i = state.items.findIndex(x => x.id === state.current.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], members: state.current.members })
    }
  },

  actions: {
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await QueuesService.list(params)
        const rows = QueuesService.unwrapList(res).map(normalizeQueue).filter(Boolean)
        commit('SET_LIST', rows)
        return rows
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchOne({ commit }, { id, params = {} }) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing queue id')

      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await QueuesService.get(endpointId, params)
        const row = normalizeQueue(QueuesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async create({ commit }, payload = {}) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await QueuesService.create(payload)
        const row = normalizeQueue(QueuesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async update({ commit }, { id, payload = {} }) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing queue id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await QueuesService.update(endpointId, payload)
        const row = normalizeQueue(QueuesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async destroy({ commit }, id) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing queue id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        await QueuesService.destroy(endpointId)
        commit('REMOVE_ONE', endpointId)
        return true
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async syncMembers({ commit, state }, { id, members }) {
      const endpointId = str(id || state.current?.id)
      if (!endpointId) throw new Error('Missing queue id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await QueuesService.syncMembers(endpointId, members || [])
        const row = normalizeQueue(QueuesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    }
  }
}
